import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, CheckCircle, Clock, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL = 30000;

function authHeader(password) {
  return { Authorization: 'Basic ' + btoa('admin:' + password) };
}

function StatusBadge({ status }) {
  if (status === 'issued') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
        <CheckCircle size={11} /> Issued
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
      <Clock size={11} /> Pending
    </span>
  );
}

export default function AdminClaimRequestsPage() {
  const [password, setPassword]   = useState('');
  const [authed, setAuthed]       = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [issuing, setIssuing]     = useState({});   // { [id]: true }
  const [issued, setIssued]       = useState({});   // { [id]: claim_code }
  const [issueErrors, setIssueErrors] = useState({});
  const passwordRef = useRef('');

  const fetchRequests = useCallback(async (pwd) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/claim-requests`, { headers: authHeader(pwd) });
      setRequests(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        setAuthError('Session expired. Please sign in again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      await axios.get(`${API}/admin/claim-requests`, { headers: authHeader(password) });
      passwordRef.current = password;
      setAuthed(true);
      fetchRequests(password);
    } catch (e) {
      setAuthError(e.response?.status === 401 ? 'Invalid password.' : 'Connection error. Try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Auto-refresh every 30 s
  useEffect(() => {
    if (!authed) return;
    const timer = setInterval(() => fetchRequests(passwordRef.current), REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [authed, fetchRequests]);

  const handleIssue = async (id) => {
    setIssuing(s => ({ ...s, [id]: true }));
    setIssueErrors(s => ({ ...s, [id]: '' }));
    try {
      const res = await axios.post(
        `${API}/admin/claim-requests/${id}/issue`,
        {},
        { headers: authHeader(passwordRef.current) }
      );
      setIssued(s => ({ ...s, [id]: res.data.claim_code }));
      // Update the local row status immediately
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'issued', claim_code: res.data.claim_code } : r));
    } catch (e) {
      setIssueErrors(s => ({ ...s, [id]: e.response?.data?.detail || 'Failed. Try again.' }));
    } finally {
      setIssuing(s => ({ ...s, [id]: false }));
    }
  };

  // ── Password gate ──────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F0EEE9] flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#0D3D4A] flex items-center justify-center">
              <Lock size={15} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-[#A8B4B8] uppercase tracking-widest font-semibold">Meridian Admin</p>
              <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Claim Requests</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="bg-white border border-stone-200 p-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">
                Admin Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 border border-[#A8B4B8] bg-[#F0EEE9] text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                data-testid="admin-password-input"
                required
                autoFocus
              />
            </div>
            {authError && (
              <p className="text-xs text-red-600" data-testid="admin-auth-error">{authError}</p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-[#0D3D4A] text-white py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {authLoading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin table ────────────────────────────────────────────────────────────
  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-8 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0D3D4A] flex items-center justify-center">
              <Lock size={14} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-[#A8B4B8] uppercase tracking-widest font-semibold">Meridian Admin</p>
              <h1 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Provider Claim Requests
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {pending > 0 && (
              <span className="text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1" data-testid="pending-count">
                {pending} pending
              </span>
            )}
            <button
              onClick={() => fetchRequests(passwordRef.current)}
              disabled={loading}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#0D3D4A] transition-colors disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            {lastRefresh && (
              <span className="text-xs text-[#A8B4B8]">
                Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="bg-white border border-stone-200 overflow-hidden" data-testid="claims-table">
          {requests.length === 0 && !loading ? (
            <div className="py-16 text-center">
              <p className="text-[#A8B4B8] text-sm font-medium">No claim requests yet</p>
              <p className="text-[#A8B4B8] text-xs mt-1">Auto-refreshes every 30 seconds</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-[#F0EEE9]/50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Facility Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Requested At</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Claim Code</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-[#F0EEE9]/40 transition-colors" data-testid={`claim-row-${req.id}`}>
                    <td className="px-6 py-4 font-medium text-stone-900">{req.facility_name}</td>
                    <td className="px-6 py-4 text-stone-600">{req.contact_email}</td>
                    <td className="px-6 py-4 text-stone-500 text-xs">
                      {new Date(req.requested_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={req.status} /></td>
                    <td className="px-6 py-4">
                      {req.claim_code ? (
                        <code className="text-xs font-mono bg-stone-100 px-2 py-1 text-[#0D3D4A] font-semibold">
                          {req.claim_code}
                        </code>
                      ) : (
                        <span className="text-[#A8B4B8] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {req.status === 'issued' ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"
                          data-testid={`issued-label-${req.id}`}
                        >
                          <CheckCircle size={13} /> Issued
                        </span>
                      ) : (
                        <div>
                          <button
                            onClick={() => handleIssue(req.id)}
                            disabled={!!issuing[req.id]}
                            className="inline-flex items-center gap-1.5 bg-[#0D3D4A] text-white text-xs font-semibold px-3 py-2 hover:bg-[#0E4557] transition-colors disabled:opacity-50"
                            data-testid={`issue-btn-${req.id}`}
                          >
                            <Send size={11} />
                            {issuing[req.id] ? 'Issuing…' : 'Issue Claim Code'}
                          </button>
                          {issueErrors[req.id] && (
                            <p className="text-xs text-red-600 mt-1" data-testid={`issue-error-${req.id}`}>
                              {issueErrors[req.id]}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-[#A8B4B8] mt-3">Auto-refreshes every 30 seconds. Total: {requests.length} request{requests.length !== 1 ? 's' : ''}.</p>
      </div>
    </div>
  );
}
